import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import {
  clearNDTIndicationLogs,
  fetchNDTIndicationLogs,
} from '../../../../../redux/ProductionData/actions';
import type { PipeOption } from '../../../../ndt/components/FilterPanel';
import { NdtRepairLogModal } from '../../../../project/components/dashboard/production/NDTIndicationLog/NdtRepairLogModal';
import { applyNdtCacheToRows, type DrillDownKpi } from '../../../utils/drillDownKpis';
import { fetchNdtIndicationLogsApi } from '../../../utils/fetchNdtIndicationLogsApi';
import {
  applyDurationDrillDownFilter,
  buildWeldDrillDownRows,
  complianceCounts,
  rowKey,
} from '../../../utils/weldDrillDownModel';
import type { DashboardMetrics } from '../../../utils/weldDashboardMetrics';
import type { OverviewWeldRow } from '../../WeldOverviewTable';
import type { WeldAnalyticsBundle } from '../../../types/weld';
import {
  collectViolationsForWeld,
  fetchPassViewRecords,
  fetchProjectLimitsApi,
  fetchTiltViewRecords,
  fetchZoneViewRecords,
  type PassViewRecord,
} from '../../../utils/fetchWeldViewApis';
import {
  type LimitsRow,
  type WpsViolationRecord,
  violationsLackLimits,
} from '../../../utils/wpsViolationModel';
import {
  getCachedWeldDetail,
  isWeldDetailCacheFresh,
  setCachedWeldDetail,
  weldDetailCacheKey,
} from '../../../utils/weldDetailCache';
import { buildDrillDownSessionKey } from '../../../utils/buildDrillDownSessionKey';
import type { PipeJobRef } from '../../../utils/jobNumbersFromSetup';
import type { WeldDrillDownPrefetchState } from '../../../utils/useWeldDrillDownPrefetch';
import { DrillDownKpiStrip } from './DrillDownKpiStrip';
import type { DurationDrillDownFilter, NdtIndicationRow, WeldDrillDownRow } from './drillDownTypes';
import { mapDrillDownRowToWeldData } from './mapDrillDownRowToWeldData';
import { WeldDrillDownRightPanel } from './WeldDrillDownRightPanel';
import { WeldDrillDownTable } from './WeldDrillDownTable';

interface DurationDrillDownHostProps {
  filter: DurationDrillDownFilter | null;
  onClose: () => void;
  metrics: DashboardMetrics;
  productionWeldData: OverviewWeldRow[];
  bundle: WeldAnalyticsBundle | null;
  statusCalculationMethod: string;
  projectId: number | null;
  pipeId: string | null;
  pipeOptions?: PipeOption[];
  canEditNdt?: boolean;
  prefetch?: WeldDrillDownPrefetchState | null;
  appliedPipeIds?: string[];
  appliedJobRefs?: PipeJobRef[];
}

function formatSec(sec: number | null): string {
  if (sec == null) return '—';
  return sec < 60 ? `${sec.toFixed(1)}s` : `${(sec / 60).toFixed(2)} min`;
}

function computeDurationKpis(rows: WeldDrillDownRow[]): DrillDownKpi[] {
  const durations = rows.map((r) => r.durationSec).filter((d): d is number => d != null);
  const avg =
    durations.length > 0 ? durations.reduce((s, d) => s + d, 0) / durations.length : null;
  const compliance = complianceCounts(rows);
  return [
    { id: 'count', label: 'Welds in selection', value: String(rows.length), accent: 'blue' },
    { id: 'avgDuration', label: 'Avg duration', value: formatSec(avg), accent: 'blue' },
    { id: 'within', label: 'Within limit', value: String(compliance.within), accent: 'green' },
    { id: 'out', label: 'Out of limit', value: String(compliance.out), accent: 'red' },
  ];
}

export const DurationDrillDownHost = ({
  filter,
  onClose,
  metrics,
  productionWeldData,
  bundle,
  statusCalculationMethod,
  projectId,
  pipeId,
  pipeOptions = [],
  canEditNdt = false,
  prefetch = null,
  appliedPipeIds = [],
  appliedJobRefs = [],
}: DurationDrillDownHostProps) => {
  const dispatch = useDispatch();
  const [selectedRow, setSelectedRow] = useState<WeldDrillDownRow | null>(null);
  const [editNdtOpen, setEditNdtOpen] = useState(false);
  const [localNdtCache, setLocalNdtCache] = useState<Record<string, NdtIndicationRow[]>>({});
  const [ndtLoadingKey, setNdtLoadingKey] = useState<string | null>(null);
  const [localWelderCache, setLocalWelderCache] = useState<Record<string, string>>({});
  const [localPassesByRowKey, setLocalPassesByRowKey] = useState<Record<string, PassViewRecord[]>>({});
  const [localViolationsByRowKey, setLocalViolationsByRowKey] = useState<
    Record<string, WpsViolationRecord[]>
  >({});
  const [wpsPassLoadingKey, setWpsPassLoadingKey] = useState<string | null>(null);
  const [wpsViolationsLoadingKey, setWpsViolationsLoadingKey] = useState<string | null>(null);
  const [localLimits, setLocalLimits] = useState<LimitsRow[]>([]);
  const [wpsDataError, setWpsDataError] = useState<string | null>(null);
  const limitsRef = useRef(localLimits);
  const ndtCacheRef = useRef(localNdtCache);
  const dataSessionKeyRef = useRef<string | null>(null);
  const limitsLoadedForSessionRef = useRef(false);
  const limitsPromiseRef = useRef<Promise<LimitsRow[]> | null>(null);

  const usingPrefetch = Boolean(prefetch);
  const ndtCache = usingPrefetch ? prefetch!.ndtCache : localNdtCache;
  const setNdtCache = usingPrefetch ? prefetch!.setNdtCache : setLocalNdtCache;
  const welderCache = usingPrefetch ? prefetch!.welderCache : localWelderCache;
  const setWelderCache = usingPrefetch ? prefetch!.setWelderCache : setLocalWelderCache;
  const passesByRowKey = usingPrefetch ? prefetch!.passesByRowKey : localPassesByRowKey;
  const setPassesByRowKey = usingPrefetch
    ? prefetch!.setPassesByRowKey
    : setLocalPassesByRowKey;
  const violationsByRowKey = usingPrefetch
    ? prefetch!.violationsByRowKey
    : localViolationsByRowKey;
  const setViolationsByRowKey = usingPrefetch
    ? prefetch!.setViolationsByRowKey
    : setLocalViolationsByRowKey;
  const limits = usingPrefetch ? prefetch!.limits : localLimits;

  const dataSessionKey = useMemo(() => {
    if (prefetch?.sessionKey) return prefetch.sessionKey;
    const pipes =
      appliedPipeIds.length > 0 ? appliedPipeIds : pipeId != null ? [pipeId] : [];
    return (
      buildDrillDownSessionKey(projectId, pipes, appliedJobRefs) ??
      (projectId != null ? `${projectId}|${pipeId ?? ''}` : null)
    );
  }, [prefetch?.sessionKey, projectId, pipeId, appliedPipeIds, appliedJobRefs]);

  limitsRef.current = limits;
  ndtCacheRef.current = ndtCache;

  const {
    ndtIndicationLogs,
    ndtIndicationLogsLoading,
    ndtIndicationLogsError,
  } = useSelector(
    (state: {
      productionData?: {
        ndtIndicationLogs?: NdtIndicationRow[];
        ndtIndicationLogsLoading?: boolean;
        ndtIndicationLogsError?: string | null;
      };
    }) => state.productionData ?? {},
  );

  const allRows = useMemo(
    () => buildWeldDrillDownRows(metrics, productionWeldData, bundle, projectId),
    [metrics, productionWeldData, bundle, projectId],
  );

  const filteredRows = useMemo(
    () => (filter ? applyDurationDrillDownFilter(allRows, filter) : []),
    [allRows, filter],
  );

  const displayRows = useMemo(
    () =>
      applyNdtCacheToRows(
        filteredRows,
        ndtCache,
        selectedRow,
        ndtIndicationLogs ?? null,
      ),
    [filteredRows, ndtCache, selectedRow, ndtIndicationLogs],
  );

  const kpis = useMemo(() => computeDurationKpis(displayRows), [displayRows]);

  useEffect(() => {
    if (dataSessionKey == null) return;
    if (dataSessionKeyRef.current === dataSessionKey) return;
    dataSessionKeyRef.current = dataSessionKey;
    if (usingPrefetch) return;
    setLocalNdtCache({});
    setLocalWelderCache({});
    setLocalPassesByRowKey({});
    setLocalViolationsByRowKey({});
    limitsRef.current = [];
    setLocalLimits([]);
    limitsLoadedForSessionRef.current = false;
    limitsPromiseRef.current = null;
  }, [dataSessionKey, usingPrefetch]);

  const ensureLimitsLoaded = useCallback(async (pid: number): Promise<LimitsRow[]> => {
    if (prefetch) {
      return prefetch.ensureLimitsLoaded(pid);
    }
    if (limitsLoadedForSessionRef.current) {
      return limitsRef.current;
    }
    if (!limitsPromiseRef.current) {
      const sessionAtFetch = dataSessionKeyRef.current;
      limitsPromiseRef.current = fetchProjectLimitsApi(pid)
        .then((data) => {
          if (dataSessionKeyRef.current !== sessionAtFetch) {
            return limitsRef.current;
          }
          limitsRef.current = data.allDataSet;
          setLocalLimits(data.allDataSet);
          limitsLoadedForSessionRef.current = true;
          return data.allDataSet;
        })
        .catch((err) => {
          limitsPromiseRef.current = null;
          throw err;
        });
    }
    return limitsPromiseRef.current;
  }, [prefetch]);

  const handleClose = useCallback(() => {
    dispatch(clearNDTIndicationLogs());
    setSelectedRow(null);
    setEditNdtOpen(false);
    setNdtLoadingKey(null);
    setWpsPassLoadingKey(null);
    setWpsViolationsLoadingKey(null);
    setWpsDataError(null);
    // Keep NDT / WPS / limits caches across duration chart navigation.
    onClose();
  }, [dispatch, onClose]);

  const loadNdtForRow = useCallback(
    async (row: WeldDrillDownRow) => {
      if (!projectId) return;
      const key = rowKey(row);
      if (key in ndtCacheRef.current) return;

      setNdtLoadingKey(key);
      try {
        const logs = await fetchNdtIndicationLogsApi({
          project_id: projectId,
          weld_id: row.weldNumber,
          ...(pipeId ? { pipe_id: Number(pipeId) } : {}),
        });
        setNdtCache((prev) => ({ ...prev, [key]: logs }));
      } catch {
        setNdtCache((prev) => ({ ...prev, [key]: [] }));
        dispatch(
          fetchNDTIndicationLogs({
            project_id: projectId,
            weld_id: row.weldNumber,
            ...(pipeId ? { pipe_id: Number(pipeId) } : {}),
          }),
        );
      } finally {
        setNdtLoadingKey((prev) => (prev === key ? null : prev));
      }
    },
    [dispatch, projectId, pipeId],
  );

  const loadWpsDataForRow = useCallback(
    async (row: WeldDrillDownRow, options?: { background?: boolean }) => {
      if (!projectId) return;
      const key = rowKey(row);
      const cacheKey = dataSessionKey ? weldDetailCacheKey(dataSessionKey, key) : null;
      const cached = cacheKey ? getCachedWeldDetail(cacheKey) : null;
      if (cached && !options?.background) {
        setPassesByRowKey((prev) => (key in prev ? prev : { ...prev, [key]: cached.passes }));
        setViolationsByRowKey((prev) =>
          key in prev ? prev : { ...prev, [key]: cached.violations },
        );
        setWelderCache((prev) => (key in prev ? prev : { ...prev, [key]: cached.welderIds }));
        if (isWeldDetailCacheFresh(cached)) return;
      }

      const hasPasses = key in passesByRowKey;
      const hasViolations = key in violationsByRowKey;
      const incomplete = hasViolations && violationsLackLimits(violationsByRowKey[key] ?? []);
      if (hasPasses && hasViolations && !incomplete) return;

      const viewParams = {
        project_id: projectId,
        project_name: row.projectName || undefined,
        weld_number: row.weldNumber,
        job_number: row.jobNumber || undefined,
      };

      let passes = passesByRowKey[key] ?? [];
      let zones: Awaited<ReturnType<typeof fetchZoneViewRecords>> = [];
      let tilts: Awaited<ReturnType<typeof fetchTiltViewRecords>> = [];

      if (!hasPasses) {
        if (!options?.background) setWpsPassLoadingKey(key);
        setWpsDataError(null);
        try {
          const interactive = { priority: 0 as const };
          passes = await fetchPassViewRecords(viewParams, interactive);
          setPassesByRowKey((prev) => ({ ...prev, [key]: passes }));
          const welderIds = Array.from(
            new Set(passes.map((p) => p.welderId).filter((id) => id && id !== '—')),
          ).join(', ');
          setWelderCache((prev) => ({ ...prev, [key]: welderIds || '—' }));
        } catch {
          setWpsDataError('Could not load pass summary for this weld.');
          setWpsPassLoadingKey((prev) => (prev === key ? null : prev));
          return;
        } finally {
          setWpsPassLoadingKey((prev) => (prev === key ? null : prev));
        }
      }

      if (hasViolations && !incomplete) {
        if (cacheKey) {
          setCachedWeldDetail(cacheKey, {
            passes,
            violations: violationsByRowKey[key] ?? [],
            welderIds: welderCache[key] ?? '—',
            loadedAt: Date.now(),
          });
        }
        return;
      }

      if (!options?.background) setWpsViolationsLoadingKey(key);
      try {
        if (!zones.length && !tilts.length) {
          const interactive = { priority: 0 as const };
          [zones, tilts] = await Promise.all([
            fetchZoneViewRecords(viewParams, interactive),
            fetchTiltViewRecords(viewParams, interactive),
          ]);
        }
        const limitsRows = await ensureLimitsLoaded(projectId);
        const violations = collectViolationsForWeld(passes, zones, tilts, limitsRows);
        setViolationsByRowKey((prev) => ({ ...prev, [key]: violations }));
        if (cacheKey) {
          setCachedWeldDetail(cacheKey, {
            passes,
            violations,
            welderIds: welderCache[key] ?? '—',
            loadedAt: Date.now(),
          });
        }
      } catch {
        setWpsDataError('Could not load WPS data for this weld.');
      } finally {
        setWpsViolationsLoadingKey((prev) => (prev === key ? null : prev));
      }
    },
    [projectId, dataSessionKey, violationsByRowKey, welderCache, passesByRowKey, ensureLimitsLoaded],
  );

  const handleRowSelect = useCallback(
    (row: WeldDrillDownRow) => {
      setSelectedRow(row);
      const key = rowKey(row);
      if (!(key in ndtCacheRef.current)) {
        void loadNdtForRow(row);
      }
      const cacheKey = dataSessionKey ? weldDetailCacheKey(dataSessionKey, key) : null;
      const cached = cacheKey ? getCachedWeldDetail(cacheKey) : null;
      if (row.outcome === 'fail') {
        loadWpsDataForRow(row, {
          background: cached != null && isWeldDetailCacheFresh(cached),
        });
      } else if (!(key in welderCache)) {
        loadWpsDataForRow(row, {
          background: cached != null && isWeldDetailCacheFresh(cached),
        });
      }
    },
    [loadNdtForRow, loadWpsDataForRow, welderCache, dataSessionKey],
  );

  const singleRowKey = displayRows.length === 1 ? rowKey(displayRows[0]) : null;

  useEffect(() => {
    if (!filter) {
      setSelectedRow(null);
      return;
    }
    if (singleRowKey && displayRows.length === 1) {
      handleRowSelect(displayRows[0]);
      return;
    }
    setSelectedRow(null);
    // Auto-open only when the filtered set becomes a single weld (or filter changes).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-fetch loops from handleRowSelect identity
  }, [filter, singleRowKey, dispatch]);

  const selectedRowKey = selectedRow ? rowKey(selectedRow) : null;
  const selectedWelderIds = selectedRowKey ? welderCache[selectedRowKey] ?? '—' : '—';
  const selectedPassRecords = selectedRowKey ? passesByRowKey[selectedRowKey] ?? [] : [];
  const selectedPassLoading = Boolean(
    selectedRowKey &&
      wpsPassLoadingKey === selectedRowKey &&
      !(selectedRowKey in passesByRowKey),
  );
  const selectedViolationsLoading = Boolean(
    selectedRowKey && wpsViolationsLoadingKey === selectedRowKey,
  );
  const selectedViolations = selectedRowKey ? violationsByRowKey[selectedRowKey] ?? [] : [];
  const showWpsViolationDetail = selectedRow?.outcome === 'fail';
  const selectedNdtLogs = selectedRowKey
    ? ndtCache[selectedRowKey] ?? ndtIndicationLogs ?? []
    : [];
  const selectedNdtLoading =
    Boolean(selectedRowKey) &&
    !(selectedRowKey && selectedRowKey in ndtCache) &&
    (ndtLoadingKey === selectedRowKey || Boolean(ndtIndicationLogsLoading));

  const open = filter != null;
  const title = filter?.label ?? 'Duration selection';

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent className="flex h-[100dvh] w-[100vw] max-h-[100dvh] max-w-[100vw] flex-col gap-3 overflow-hidden rounded-none border-0 p-4 sm:p-5">
          {filter && (
            <>
              <DialogHeader className="shrink-0">
                <DialogTitle className="font-heading text-blueprint-blue">
                  {title}
                  <span className="ml-2 rounded-full bg-[rgba(0,39,63,0.06)] px-2.5 py-0.5 text-sm font-semibold text-heritage-grey">
                    {displayRows.length}
                  </span>
                </DialogTitle>
              </DialogHeader>

              <div className="shrink-0">
                <DrillDownKpiStrip kpis={kpis} />
              </div>

              <div className="grid min-h-0 flex-1 gap-4 overflow-hidden lg:grid-cols-5">
                <div className="flex min-h-0 flex-col lg:col-span-2">
                  <WeldDrillDownTable
                    rows={displayRows}
                    selectedRowKey={selectedRowKey}
                    onRowSelect={handleRowSelect}
                    showOutcomeColumn
                    showViolationColumns={false}
                  />
                </div>
                <div className="flex min-h-0 flex-col lg:col-span-3">
                  {wpsDataError && selectedRow && (
                    <p className="mb-2 shrink-0 text-sm text-readable-red">{wpsDataError}</p>
                  )}
                  <WeldDrillDownRightPanel
                    selectedRow={selectedRow}
                    statusCalculationMethod={statusCalculationMethod}
                    ndtLogs={selectedNdtLogs}
                    ndtLoading={selectedNdtLoading}
                    ndtError={ndtIndicationLogsError ?? null}
                    canEditNdt={canEditNdt}
                    onEditNdt={() => selectedRow && setEditNdtOpen(true)}
                    welderIds={selectedWelderIds}
                    passesLoading={selectedPassLoading}
                    passRecords={selectedPassRecords}
                    wpsViolations={selectedViolations}
                    wpsViolationsLoading={selectedViolationsLoading}
                    showWpsViolationDetail={showWpsViolationDetail}
                    dashboardProjectId={projectId}
                    pipeId={
                      selectedRow?.productionRow?.pipeId != null
                        ? String(selectedRow.productionRow.pipeId)
                        : selectedRow?.productionRow?.pipe_details_id != null
                          ? String(selectedRow.productionRow.pipe_details_id)
                          : pipeId
                    }
                    pipeOptions={pipeOptions}
                  />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {selectedRow && editNdtOpen && (
        <NdtRepairLogModal
          isOpen={editNdtOpen}
          onClose={() => setEditNdtOpen(false)}
          weld={mapDrillDownRowToWeldData(
            selectedRow,
            selectedRow.productionRow?.pipeId != null
              ? String(selectedRow.productionRow.pipeId)
              : selectedRow.productionRow?.pipe_details_id != null
                ? String(selectedRow.productionRow.pipe_details_id)
                : pipeId,
            pipeOptions,
          )}
          canEdit={canEditNdt}
        />
      )}
    </>
  );
};
